import React, { useContext, useMemo, useState } from 'react';
import {
  ChiroUpBaseCommon,
  ChiroUpDayJsCommon,
  InvoiceDataHistoryType,
} from '@chiroup/core';
import {
  Checkbox,
  HashDisplay,
  Textarea,
  TrivialDownload,
} from '@chiroup/components';
import classNames from 'classnames';
import CopyToClipboard from 'react-copy-to-clipboard';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { ToastContext, ToastTypes } from '../../contexts/toast.context';
import { MeContext } from '../../contexts/me.context';

interface Props {
  history: InvoiceDataHistoryType | null;
}

const fieldsetClassName =
  'border border-gray-400 p-4 bg-white rounded-md mb-4 flex flex-col w-full';
const legendClassName = 'px-1';

const InvoiceDataDetails: React.FC<Props> = ({ history }) => {
  const { selectedLocationFull } = useContext(MeContext);
  const { createToast } = useContext(ToastContext);
  const [toggleNewLineSegments, setToggleNewLineSegments] = useState(true);
  const ediString = useMemo(() => {
    if (!history) return '';
    const segments = history?.segments ?? [],
      sep = toggleNewLineSegments ? '~\n' : '~';
    return segments.join(sep) + sep;
  }, [history, toggleNewLineSegments]);

  if (!history) return null;

  return (
    <div className="mx-8 my-2 p-2 rounded-lg bg-white border border-gray-400 relative mb-8">
      <HashDisplay
        header="Invoice History"
        valueClassNames={{
          'Billed Amount': 'px-4 py-2 text-sm text-right',
          Balance: 'px-4 py-2 text-sm text-right',
          Status: 'px-4 py-2 text-sm text-right',
          'Date and Time': 'px-4 py-2 text-sm text-right',
        }}
        hash={{
          'Billed Amount': ChiroUpBaseCommon.format.asMoney(
            history.billedAmount,
          ),
          Balance: ChiroUpBaseCommon.format.asMoney(history.balance),
          Status: history.status,
          'Date and Time': ChiroUpDayJsCommon.display.datetime(
            history.timestamp,
            selectedLocationFull?.timezone ??
              ChiroUpDayJsCommon.defaultTimezone,
          ),
        }}
      />
      {history?.segments ? (
        <div className="mt-4 mx-8 relative">
          <fieldset className={classNames(fieldsetClassName, 'mt-4 relative')}>
            <legend className={legendClassName}>837P</legend>
            <div className="absolute top-0 right-0 flex flex-row">
              <div className="flex mt-1 z-50">
                <Checkbox
                  name={'toggleNewLineSegments'}
                  value={toggleNewLineSegments}
                  onChange={() => {
                    setToggleNewLineSegments(!toggleNewLineSegments);
                  }}
                  label={`Split Response Segments`}
                  tooltip={[
                    `When copied or downloaded, the segments ${
                      toggleNewLineSegments ? 'will' : 'will not'
                    } have newlines between them.`,
                    'Newlines makes it easier for humans to read, but the payload',
                    'is more compact without them. Whether these matter during',
                    'an EDI transmission depends on the receiving party.',
                  ].join(' ')}
                />
              </div>
              <div className="flex">
                <TrivialDownload
                  text={ediString}
                  containerClassName="mr-7 cursor-pointer"
                />
              </div>
              <div className="flex mr-4">
                <CopyToClipboard
                  text={ediString}
                  onCopy={() => {
                    createToast({
                      title: 'Success!',
                      description: <>Text copied to clipboard!</>,
                      type: ToastTypes.Success,
                      duration: 5000,
                    });
                  }}
                  options={{ format: 'text/plain' }}
                >
                  <ClipboardDocumentIcon
                    className="h-6 w-6 cursor-pointer print:hidden hover:text-primary-600"
                    title="Copy as plain text"
                  />
                </CopyToClipboard>
              </div>
            </div>
            <Textarea
              label=" "
              name="ediString"
              value={ediString}
              rows={24}
              className="mt-4 w-full font-mono text-xs"
              disabled={true}
              onChange={function (_: string): void {
                throw new Error('Function not implemented.');
              }}
            />
          </fieldset>
        </div>
      ) : null}
      {/* <pre>{ChiroUpJSON.pretty(history)}</pre> */}
    </div>
  );
};

export default InvoiceDataDetails;
